import classNames from 'classnames';
import { App, Tooltip } from 'antd';
import { LuCalendarArrowDown, LuChevronRight, LuChevronLeft } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext, useSelfContext } from '@/context';
import { useProgramContext } from '@/context/ProgramContext';
import { PageContainer, CreditButton, Flex } from '@/components/common';
import { useDevice, useLocalStorage, useModal, useReserveVotables } from '@/hooks';
import { SelfRatingModal } from '@/components/modals';
import ChangeSelfModal from './ChangeSelfModal';
import ReserveHeader from './ReserveHeader';
import ReserveProgramDesktop from './ReserveProgramDesktop';
import ReserveProgramMobile from './ReserveProgramMobile';
import useWeekManagement from './useWeekManagement';
import ReserveDetailModal from './ReserveDetailModal';
import RateButton from '../RateButton';
import { envConfig } from '@/configs';
import { useLocaleStore } from '@/store';
import SelectReserveModeModal from './SelectReserveModeModal';

import './styles.scss';

export interface ReserveProgramProps {
  variant: AppModules;
}

export function ReserveProgram({ variant }: ReserveProgramProps) {
  const conf = envConfig.onPremise;
  const [isNewReserveMode, setIsNewReserveMode] = useLocalStorage('new-reserve-mode-api', null);
  const navigate = useNavigate();
  const [locked, setLocked] = useState(false);
  const [t] = useTranslation(['common', 'reserve']);
  const { mode } = useAppContext();
  const { message } = App.useApp();
  const { isDesktop } = useDevice();
  const selfModal = useModal();
  const rateModal = useModal();
  const detailModal = useModal();
  const changeSelfModal = useModal();
  const selectReserveModeModal = useModal();
  const { currentSelf, selfText, redirectToSelf } = useSelfContext();
  const { programs, registerFetchCallback, groupedPrograms, fetchPrograms, isProgramsFetching } = useProgramContext();
  const { date, dates, dateEN, nextWeek, prevWeek, goToCurrentWeek } = useWeekManagement();
  const {
    submit,
    rateReasons,
    votableItems,
    isFetched,
    fetch: fetchVotable,
    loading: votableLoading,
  } = useReserveVotables();

  useEffect(() => {
    if (conf.core.changableReserveMode === true && currentSelf) {
      if (localStorage.getItem('new-reserve-mode-api')) {
        selectReserveModeModal.show();
      }
    }
  }, [currentSelf]);

  const onRateButtonClick = useCallback(() => {
    if (votableItems.length === 0) {
      message.warning({
        key: 'not-votable-for-rate',
        content: t('reserve:errors.no-polls'),
      });
    }
    rateModal.show();
  }, [rateModal, votableItems]);

  const fetchCurrentSelfPrograms = useCallback(() => {
    if (currentSelf?.id) {
      return fetchPrograms(currentSelf.id, dateEN);
    }
    return Promise.resolve();
  }, [dateEN, fetchPrograms, currentSelf?.id, isNewReserveMode]);

  useEffect(() => {
    registerFetchCallback(fetchCurrentSelfPrograms);
  }, [fetchCurrentSelfPrograms, registerFetchCallback]);

  useEffect(() => {
    fetchCurrentSelfPrograms();
  }, [fetchCurrentSelfPrograms]);

  useEffect(() => {
    if (currentSelf == null) {
      if (isDesktop()) {
        selfModal.show();
      }
      else {
        redirectToSelf({ mode, navigate });
      }
    }
  }, [currentSelf, isDesktop]);

  useEffect(() => {
    if (currentSelf && !isFetched) {
      fetchVotable().then(rateModal.show);
    }
  }, [currentSelf, isFetched]);

  const isMobile = !isDesktop();
  const isRTL = useLocaleStore(state => state.dir) === 'rtl';
  const rateButton = (
    <RateButton
      size={isMobile ? 'small' : 'middle'}
      onClick={onRateButtonClick}
      className={isMobile ? 'week-nav-rate-btn-mobile' : classNames('week-nav-rate-btn', `week-nav-rate-btn-${variant}`)}
    />
  );

  // Week navigation buttons - only for desktop
  const weekNavButtons = useMemo(() => {
    if (isMobile) return null;

    const prevButton = (
      <Tooltip title={t('common:labels.prev-week')} placement="bottomRight">
        <Flex
          className={classNames('week-nav-btn-compact', `week-nav-btn-compact-${variant}`)}
          onClick={() => !isProgramsFetching && prevWeek()}
          align="center"
          justify="center"
        >
          {isRTL ? <LuChevronRight className="text-white text-xl" /> : <LuChevronLeft className="text-white text-sm" />}
        </Flex>
      </Tooltip>
    );

    const currentWeekButton = (
      <Tooltip title={t('common:labels.current-week')} placement="bottom">
        <Flex
          className={classNames('week-nav-btn-current', `week-nav-btn-current-${variant}`)}
          onClick={() => !isProgramsFetching && goToCurrentWeek()}
          align="center"
          justify="center"
        >
          <LuCalendarArrowDown className="text-white text-xl" />
        </Flex>
      </Tooltip>
    );

    const nextButton = (
      <Tooltip title={t('common:labels.next-week')} placement="bottom">
        <Flex
          className={classNames('week-nav-btn-compact', `week-nav-btn-compact-${variant}`)}
          onClick={() => !isProgramsFetching && nextWeek()}
          align="center"
          justify="center"
        >
          {isRTL ? <LuChevronLeft className="text-white text-xl" /> : <LuChevronRight className="text-white text-sm" />}
        </Flex>
      </Tooltip>
    );

    return (
      <>
        {prevButton}
        {currentWeekButton}
        {nextButton}
      </>
    );
  }, [isMobile, variant, isRTL, isProgramsFetching, prevWeek, nextWeek, goToCurrentWeek, t]);

  // End element with all buttons wrapped in a circle
  const endElement = useMemo(() => {
    if (isMobile) {
      return envConfig.ui.removeWallet ? null : <CreditButton />;
    }
    return (
      <Flex className={classNames('week-nav-buttons-wrapper', `week-nav-buttons-wrapper-${variant}`)} align="center" gap={8}>
        {weekNavButtons}
        {rateButton}
      </Flex>
    );
  }, [isMobile, weekNavButtons, rateButton, variant]);

  return (
    <PageContainer
      noPadding
      hideScrollbar={!isDesktop()}
      title={selfText('reserve-title')}
      desktopCenterElement={<ReserveHeader isNewReserveMode={isNewReserveMode} setIsNewReserveMode={setIsNewReserveMode} />}
      endElement={endElement}
      bodyClassName={`reserve-page-${variant}`}
      containerClassName={isMobile ? '!overflow-hidden' : ''}
    >
      <SelfRatingModal
        type={variant}
        hide={rateModal.hide}
        open={rateModal.open}
        items={votableItems}
        reasons={rateReasons}
        loading={votableLoading}
        submit={submit}
      />
      <ReserveDetailModal variant={variant} {...detailModal} />
      <ChangeSelfModal
        variant={variant}
        onSuccess={fetchCurrentSelfPrograms}
        {...changeSelfModal}
      />
      {
        isNewReserveMode === null && (
          <SelectReserveModeModal
            hide={selectReserveModeModal.hide}
            open={selectReserveModeModal.open}
            setIsNewReserveMode={setIsNewReserveMode}
          />
        )
      }

      {currentSelf && conf.core.changableReserveMode === true ? (
        <>
          {isMobile ? (
            <>
              <ReserveHeader mobileButton={rateButton} isNewReserveMode={isNewReserveMode} setIsNewReserveMode={setIsNewReserveMode} />
              <ReserveProgramMobile
                isNewReserveMode={isNewReserveMode}
                panelWeekStartDate={dateEN}
                date={date}
                dates={dates}
                variant={variant}
                programs={programs}
                nextWeek={nextWeek}
                prevWeek={prevWeek}
                locked={locked}
                setLocked={setLocked}
                detailModal={detailModal}
                changeSelfModal={changeSelfModal}
                isProgramsFetching={isProgramsFetching}
              />
            </>
          ) : (
            <ReserveProgramDesktop
              isNewReserveMode={isNewReserveMode}
              panelWeekStartDate={dateEN}
              variant={variant}
              programs={programs}
              groupedPrograms={groupedPrograms}
              dates={dates}
              detailModal={detailModal}
              changeSelfModal={changeSelfModal}
              isProgramsFetching={isProgramsFetching}
              locked={locked}
              setLocked={setLocked}
            />
          )}
        </>
      ) : (
        <>
          {isMobile ? (
            <>
              <ReserveHeader mobileButton={rateButton} isNewReserveMode={false} setIsNewReserveMode={() => { }} />
              <ReserveProgramMobile
                panelWeekStartDate={dateEN}
                date={date}
                dates={dates}
                variant={variant}
                programs={programs}
                nextWeek={nextWeek}
                prevWeek={prevWeek}
                locked={locked}
                setLocked={setLocked}
                detailModal={detailModal}
                changeSelfModal={changeSelfModal}
                isProgramsFetching={isProgramsFetching}
              />
            </>
          ) : (
            <ReserveProgramDesktop
              panelWeekStartDate={dateEN}
              variant={variant}
              programs={programs}
              groupedPrograms={groupedPrograms}
              dates={dates}
              detailModal={detailModal}
              changeSelfModal={changeSelfModal}
              isProgramsFetching={isProgramsFetching}
              locked={locked}
              setLocked={setLocked}
            />
          )}
        </>
      )}
    </PageContainer>
  );
}

export default ReserveProgram;
